import { Link } from "wouter";
import { TrendingUp, TrendingDown } from "lucide-react";
import type { Product } from "@shared/types";
import { getProductPath } from "@shared/productRouting";
import { useProductAnalytics } from "@/hooks/useProductAnalytics";
import { toDisplayAssetUrl } from "@/lib/assets";
import { parseResolvedMarketChange } from "@/lib/marketChange";

function formatPrice(cents: number) {
  return `$${cents.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function preventDefault(event: { preventDefault: () => void }) {
  event.preventDefault();
}

function getProductImageSizeClassName(
  product: Pick<Product, "brand" | "marketModel" | "deviceType">,
  options: { isStaticCard: boolean; isCompactStaticCard: boolean }
) {
  const baseClassName = `relative z-[1] max-h-[72%] max-w-[58%] object-contain object-center transition-all duration-500 ${
    options.isStaticCard
      ? `${options.isCompactStaticCard ? "max-h-[60%] sm:max-h-[72%] max-w-[50%] sm:max-w-[58%]" : ""} select-none`
      : "group-hover:scale-[1.03] select-none"
  }`;

  const normalizedBrand = product.brand.trim().toLowerCase();
  const normalizedModel = product.marketModel.trim().toLowerCase();

  if (normalizedBrand === "meta" && product.deviceType === "vr_headsets") {
    if (normalizedModel === "quest 3") {
      return options.isStaticCard
        ? `relative z-[1] ${
            options.isCompactStaticCard
              ? "max-h-[44%] sm:max-h-[56%] max-w-[36%] sm:max-w-[46%] object-contain object-center transition-all duration-500 select-none"
              : "max-h-[56%] max-w-[46%] object-contain object-center transition-all duration-500 select-none"
          }`
        : "relative z-[1] max-h-[56%] max-w-[46%] object-contain object-center transition-all duration-500 group-hover:scale-[1.01] select-none";
    }

    if (normalizedModel === "quest 2") {
      return options.isStaticCard
        ? `relative z-[1] ${
            options.isCompactStaticCard
              ? "max-h-[42%] sm:max-h-[54%] max-w-[34%] sm:max-w-[44%] object-contain object-center transition-all duration-500 select-none"
              : "max-h-[54%] max-w-[44%] object-contain object-center transition-all duration-500 select-none"
          }`
        : "relative z-[1] max-h-[54%] max-w-[44%] object-contain object-center transition-all duration-500 group-hover:scale-[1.01] select-none";
    }
  }

  return baseClassName;
}

function AppleMark({
  className = "ml-1 block h-5 w-auto object-contain",
}: {
  className?: string;
}) {
  return (
    <img
      src="https://cdn.techfare.com/Brand%20Logos/Apple_logo_black.svg.png"
      alt="Apple"
      className={className}
    />
  );
}

function SamsungMark() {
  return (
    <span className="inline-flex items-center">
      <img
        src="https://cdn.techfare.com/Brand%20Logos/Samsung_wordmark.svg.png"
        alt="Samsung"
        className="h-4 w-14 object-contain object-left"
      />
    </span>
  );
}

function NintendoMark() {
  return (
    <span className="inline-flex items-center">
      <img
        src="https://cdn.techfare.com/Brand%20Logos/Nintendo.svg.png"
        alt="Nintendo"
        className="h-5 w-auto object-contain"
      />
    </span>
  );
}

function SonyMark() {
  return (
    <span className="inline-flex items-center rounded-md bg-white/96 px-1.5 py-0.5 text-[7px] font-black uppercase tracking-[0.2em] text-slate-900 shadow-[0_8px_18px_rgba(15,23,42,0.12)] backdrop-blur-md">
      Sony
    </span>
  );
}

function PlayStationMark() {
  return (
    <span className="inline-flex items-center">
      <img
        src="https://cdn.techfare.com/Brand%20Logos/PlayStation_logo.svg.png"
        alt="PlayStation"
        className="h-5 w-auto object-contain"
      />
    </span>
  );
}

function BoseMark() {
  return (
    <span className="inline-flex items-center">
      <img
        src="https://cdn.techfare.com/Brand%20Logos/Bose_logo.svg.png"
        alt="Bose"
        className="h-4 w-14 object-contain object-left"
      />
    </span>
  );
}

function XboxMark() {
  return (
    <span className="inline-flex items-center">
      <img
        src="https://cdn.techfare.com/Brand%20Logos/Xbox_Logo.svg.png"
        alt="Xbox"
        className="h-5 w-auto object-contain"
      />
    </span>
  );
}

function MetaMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className="inline-flex items-center">
      <img
        src="https://cdn.techfare.com/Brand%20Logos/Meta_lockup_positive%20primary_RGB.png"
        alt="Meta"
        className={compact ? "h-4 w-auto object-contain" : "h-7 w-auto object-contain"}
      />
    </span>
  );
}

function GoogleMark() {
  return (
    <span className="inline-flex items-center">
      <img
        src="https://www.gstatic.com/marketing-cms/assets/images/3e/42/ffe1c65342e2b864eab97a7b5ccd/super-g-pixel.webp=n-w76-h96-fcrop64=1,00000000ffffffff-rw"
        alt="Google"
        className="h-7 w-7 object-contain"
      />
    </span>
  );
}

export default function ProductCard({
  product,
  title,
  priceChangePercentOverride,
  analyticsSource,
  interactionMode = "default",
  compactMobile = false,
  showBidAsk = true,
}: {
  product: Product;
  title?: string;
  priceChangePercentOverride?: string;
  analyticsSource?: string;
  interactionMode?: "default" | "static";
  compactMobile?: boolean;
  showBidAsk?: boolean;
}) {
  const { trackProductClick } = useProductAnalytics();
  const productImageSrc = toDisplayAssetUrl(product.imageUrl);
  const priceChangePercent = priceChangePercentOverride ?? product.priceChangePercent24h;
  const resolvedChangePercent = parseResolvedMarketChange(priceChangePercent);
  const hasResolvedChange = resolvedChangePercent.hasResolvedChange;
  const changePercent = resolvedChangePercent.value ?? 0;
  const isPositive = changePercent >= 0;
  const hasCurrentMarketPrice = product.currentPrice > 0;
  const isApple = product.brand.toLowerCase() === "apple";
  const isSamsung = product.brand.toLowerCase() === "samsung";
  const isNintendo = product.brand.toLowerCase() === "nintendo";
  const isSony = product.brand.toLowerCase() === "sony";
  const isPlayStation =
    product.brand.toLowerCase() === "playstation" ||
    (isSony && product.name.toLowerCase().includes("playstation"));
  const isMicrosoft = product.brand.toLowerCase() === "microsoft";
  const isMeta = product.brand.toLowerCase() === "meta";
  const isGoogle = product.brand.toLowerCase() === "google";
  const isBose = product.brand.toLowerCase() === "bose";
  const isMetaVrHeadset = isMeta && product.deviceType === "vr_headsets";
  const isStaticCard = interactionMode === "static";
  const isCompactStaticCard = isStaticCard && compactMobile;
  const whiteImagePanelClass = isStaticCard
    ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(247,249,252,0.98))] dark:bg-[linear-gradient(180deg,rgba(18,18,20,0.98),rgba(5,5,7,0.99))]"
    : "bg-white";
  const cardShellClass = isStaticCard
    ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(248,250,252,0.99))] dark:bg-[linear-gradient(180deg,rgba(10,10,12,0.995),rgba(2,2,3,1))]"
    : "bg-card";
  const infoAreaClass = isStaticCard
    ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(246,248,251,0.98))] dark:bg-[linear-gradient(180deg,rgba(12,12,14,0.96),rgba(3,3,4,1))]"
    : "bg-white border-t border-slate-100/80";
  const titleTextClass = isStaticCard ? "text-slate-900 dark:text-slate-50" : "text-foreground";
  const labelTextClass = isStaticCard ? "text-slate-500 dark:text-slate-400" : "text-foreground";
  const valueTextClass = isStaticCard ? "text-slate-900 dark:text-slate-50" : "text-foreground";
  const metaTextClass = isStaticCard ? "text-slate-500 dark:text-slate-400" : "text-muted-foreground";
  const imageAreaClass = isStaticCard
    ? `relative overflow-hidden ${whiteImagePanelClass} ${
        isCompactStaticCard ? "aspect-[0.9] sm:aspect-[1.12]" : "aspect-[1.12]"
      }`
    : `relative aspect-[1.12] overflow-hidden ${whiteImagePanelClass}`;
  const imageShellClass = isStaticCard
    ? `absolute inset-0 flex items-center justify-center overflow-hidden ${whiteImagePanelClass}`
    : `absolute inset-0 flex items-center justify-center overflow-hidden border-b border-slate-200 ${whiteImagePanelClass}`;
  const productImageClassName = getProductImageSizeClassName(product, {
    isStaticCard,
    isCompactStaticCard,
  });
  const useCompactMetaBadge = isStaticCard && isMetaVrHeadset;

  return (
    <Link
      href={getProductPath(product)}
      onClick={() =>
        trackProductClick(product.id, analyticsSource ?? "product-card")
      }
    >
      <div
        className={`group relative overflow-hidden rounded-[0.85rem] ${cardShellClass}`}
      >
        {/* Image area */}
        <div className={imageAreaClass}>
          {product.imageUrl ? (
            <div className={`${imageShellClass} product-cutout-frame`}>
              <img
                src={productImageSrc}
                alt={product.name}
                draggable={false}
                className={productImageClassName}
                onContextMenu={preventDefault}
                onDragStart={preventDefault}
                loading={isStaticCard ? "lazy" : "eager"}
                decoding="async"
                fetchPriority={isStaticCard ? "low" : "auto"}
              />
            </div>
          ) : (
            <div className={imageShellClass}>
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] ${
                  isStaticCard
                    ? "bg-slate-100 dark:bg-white/[0.06] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                    : "bg-muted/90 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
                }`}
              >
                <span
                  className={`text-xl font-bold ${
                    isStaticCard ? "text-slate-500 dark:text-zinc-300" : "text-muted-foreground"
                  }`}
                >
                  {product.brand[0]}
                </span>
              </div>
            </div>
          )}
          {!product.imageUrl && (
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
          )}

          {/* Category badge */}
          <div className="absolute left-2.5 top-2.5 z-10">
            {isApple ? (
              <span className="inline-flex items-center drop-shadow-[0_6px_12px_rgba(0,0,0,0.35)]">
                <AppleMark />
              </span>
            ) : isSamsung ? (
              <SamsungMark />
            ) : isNintendo ? (
              <NintendoMark />
            ) : isPlayStation ? (
              <PlayStationMark />
            ) : isSony ? (
              <SonyMark />
            ) : isMicrosoft ? (
              <XboxMark />
            ) : isMeta ? (
              <MetaMark compact={useCompactMetaBadge} />
            ) : isGoogle ? (
              <GoogleMark />
            ) : isBose ? (
              <BoseMark />
            ) : (
              <span
                className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[6px] font-semibold uppercase tracking-[0.18em] backdrop-blur-md shadow-[0_10px_24px_rgba(15,23,42,0.16)] ${
                  isStaticCard
                    ? "border border-slate-200 bg-white/95 text-slate-700 dark:border-white/8 dark:bg-white/[0.04] dark:text-zinc-200"
                    : "border border-border bg-popover/92 text-popover-foreground dark:shadow-[0_10px_24px_rgba(0,0,0,0.32)]"
                }`}
              >
                {product.brand}
              </span>
            )}
          </div>

          {/* Change badge */}
          <div
            className={`absolute right-1.5 z-10 ${isApple ? "top-0.5" : "top-1.5"}`}
          >
            <span
              className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-[2px] text-[9px] font-semibold backdrop-blur-md ${
                !hasResolvedChange
                  ? "bg-popover/88 text-muted-foreground"
                  : isPositive
                    ? "bg-emerald-500/10 text-emerald-500"
                    : "bg-destructive/10 text-destructive"
              }`}
            >
              {!hasResolvedChange ? null : isPositive ? (
                <TrendingUp className="h-2.5 w-2.5" />
              ) : (
                <TrendingDown className="h-2.5 w-2.5" />
              )}
              {!hasResolvedChange
                ? "--"
                : `${isPositive ? "+" : ""}${changePercent.toFixed(1)}%`}
            </span>
          </div>
        </div>

        {/* Info area */}
        <div
          className={`relative ${infoAreaClass} space-y-0.5 ${
            isCompactStaticCard ? "p-1 sm:p-2" : "p-2"
          }`}
        >
          <h3
            className={`font-semibold line-clamp-2 leading-snug transition-colors ${titleTextClass} ${
              isCompactStaticCard
                ? "line-clamp-1 text-[0.68rem] sm:line-clamp-2 sm:text-[0.85rem]"
                : "text-[0.78rem] sm:text-[0.85rem]"
            } ${
              isStaticCard ? "" : "group-hover:text-primary"
            }`}
          >
            {title ?? product.name}
          </h3>

          <div className="flex items-end justify-between">
            <div className={isCompactStaticCard ? "pt-0 sm:pt-1.5" : "pt-1 sm:pt-1.5"}>
              <p
                className={`mb-0.5 uppercase tracking-wider ${labelTextClass} ${
                  isCompactStaticCard ? "text-[7px]" : "text-[8px]"
                }`}
              >
                Market Price
              </p>
              <p
                className={`font-mono font-bold ${valueTextClass} ${
                  isCompactStaticCard
                    ? "text-[0.94rem] sm:text-[1.18rem]"
                    : "text-[1.1rem] sm:text-[1.18rem]"
                }`}
              >
                {hasCurrentMarketPrice ? formatPrice(product.currentPrice) : "—"}
              </p>
            </div>
            <div className="text-right">
              <p
                className={`mb-0.5 uppercase tracking-wider ${labelTextClass} ${
                  isCompactStaticCard ? "text-[7px]" : "text-[8px]"
                }`}
              >
                Retail
              </p>
              <p
                className={`font-mono ${valueTextClass} ${
                  isCompactStaticCard
                    ? "text-[9px] sm:text-[12px]"
                    : "text-[11px] sm:text-[12px]"
                }`}
              >
                {product.retailPrice ? formatPrice(product.retailPrice) : "—"}
              </p>
            </div>
          </div>

          {showBidAsk ? (
            <div className={`flex gap-1 ${isCompactStaticCard ? "pt-0" : "pt-1"}`}>
              <div
                className={`flex-1 rounded-md border border-emerald-500/25 bg-emerald-500/10 text-center ${
                  isCompactStaticCard ? "px-1 py-0" : "px-1 py-0.5"
                }`}
              >
                <p className="mb-0.5 text-[6px] uppercase tracking-wider text-emerald-500/70">
                  Bid
                </p>
                <p
                  className={`font-mono font-bold text-emerald-500 ${
                    isCompactStaticCard
                      ? "text-[7px] sm:text-[10px]"
                      : "text-[9px] sm:text-[10px]"
                  }`}
                >
                  {product.highestBid ? formatPrice(product.highestBid) : "—"}
                </p>
              </div>
              <div
                className={`flex-1 rounded-md border border-destructive/20 bg-destructive/10 text-center ${
                  isCompactStaticCard ? "px-1 py-0" : "px-1 py-0.5"
                }`}
              >
                <p className="mb-0.5 text-[6px] uppercase tracking-wider text-destructive/70">
                  Ask
                </p>
                <p
                  className={`font-mono font-bold text-destructive ${
                    isCompactStaticCard
                      ? "text-[7px] sm:text-[10px]"
                      : "text-[9px] sm:text-[10px]"
                  }`}
                >
                  {product.lowestAsk ? formatPrice(product.lowestAsk) : "—"}
                </p>
              </div>
            </div>
          ) : null}

          <div
            className={`${metaTextClass} ${
              isCompactStaticCard
                ? "hidden items-center justify-between gap-2 pt-0.5 text-[9px] sm:flex sm:pt-1 sm:text-[10px]"
                : "flex items-center justify-between gap-2 pt-1 text-[10px]"
            }`}
          >
            <span />
            <span>
              {product.listingCount} live listing
              {product.listingCount === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
